import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';
import { setCookie } from '../../utils/cookieUtils';

type SignInState = {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  firstLogin: boolean;
};

const initialState: SignInState = {
  loading: false,
  error: false,
  errorMessage: null,
  firstLogin: false,
};

interface SignInPayload {
  username: string;
  password: string;
}

export const signIn = createAsyncThunk<
  unknown,
  SignInPayload,
  { rejectValue: string }
>('signIn', async (credentials, thunkAPI) => {
  try {
    const { data } = await api.post('/api/account/signin', credentials);
    return data;
  } catch (error: any) {
    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      'Sign in failed';
    return thunkAPI.rejectWithValue(message);
  }
});

const signInSlice = createSlice({
  name: 'signIn',
  initialState,
  reducers: {
    resetSignIn: (state) => {
      state.loading = false;
      state.error = false;
      state.errorMessage = null;
      state.firstLogin = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signIn.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = null;
      state.firstLogin = false;
    });
    builder.addCase(signIn.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.errorMessage = null;
      state.firstLogin = Boolean((action.payload as any)?.first_login);
      const accessToken = (action.payload as any)?.access_token;
      if (accessToken) {
        setCookie('access_token', accessToken, 7); // Set for 7 days
      }
    });
    builder.addCase(signIn.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.payload ?? 'Sign in failed';
    });
  },
});

export const { resetSignIn } = signInSlice.actions;
export default signInSlice.reducer;
